/**
 * ProtocolsPanel — kompakte Liste + „Neues Protokoll"-Button, wiederverwendbar
 * für Mietverhältnis-Detail und Einheit-Detail. Filtert Protokolle über die
 * mitgegebenen Kontext-IDs (`tenancyId` ODER `unitId`).
 */
import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Eye, Download, PencilSimple, Trash, ShieldCheck, ClipboardText, Printer, UploadSimple } from "@phosphor-icons/react";
import ProtocolWizard from "@/components/ProtocolWizard";
import DocumentPreviewDrawer from "@/components/DocumentPreviewDrawer";

const TYPE_LABELS = {
  handover_in: "Übergabe (Einzug)",
  handover_out: "Rücknahme (Auszug)",
  key_handover: "Schlüsselübergabe",
  inspection: "Begehung",
  document_handover: "Dokumentenübergabe",
  interim_inspection: "Zwischenabnahme",
  trade_acceptance: "Baugewerke-Abnahme",
};

export default function ProtocolsPanel({ tenancyId, propertyId, unitId, initialType }) {
  const [items, setItems] = useState([]);
  const [openWizard, setOpenWizard] = useState(false);
  const [wizardInitial, setWizardInitial] = useState({});
  const [loading, setLoading] = useState(false);

  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [previewId, setPreviewId] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tenancyId) params.tenancy_id = tenancyId;
      if (propertyId) params.property_id = propertyId;
      if (unitId) params.unit_id = unitId;
      const [{ data: proto }, { data: docs }] = await Promise.all([
        api.get("/protocols", { params }),
        api.get("/documents", { params: { ...params, categories: "Übergabeprotokoll,protocol" } }).catch(() => ({ data: [] })),
      ]);
      setItems(proto || []);
      setUploadedDocs(docs || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [tenancyId, propertyId, unitId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setWizardInitial({
      tenancy_id: tenancyId || null,
      property_id: propertyId || null,
      unit_id: unitId || null,
      ...(initialType ? { protocol_type: initialType } : {}),
    });
    setOpenWizard(true);
  };
  const openEdit = async (id) => {
    try {
      const { data } = await api.get(`/protocols/${id}`);
      setWizardInitial(data);
      setOpenWizard(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openPdf = async (id, signed) => {
    const url = `/protocols/${id}/${signed ? "download-pdf" : "preview-pdf"}`;
    const { data } = await api.get(url, { responseType: "blob" });
    window.open(URL.createObjectURL(data), "_blank");
  };
  const openBlank = async (id) => {
    try {
      const { data } = await api.get(`/protocols/${id}/blank-pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const fileInputRefs = useRef({});
  const triggerUpload = (id) => {
    if (!fileInputRefs.current[id]) return;
    fileInputRefs.current[id].click();
  };
  const doUploadSigned = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/protocols/${id}/upload-signed`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Unterschriebenes Original übernommen — Protokoll ist signiert");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!await window._domivusConfirm("Entwurf wirklich löschen?")) return;
    try { await api.delete(`/protocols/${id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="protocols-panel">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
          <ClipboardText size={16} className="text-emerald-600" />
          Protokolle {items.length > 0 && <span className="text-slate-400 font-normal">({items.length})</span>}
        </div>
        <Button size="sm" onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"
                data-testid="protocols-panel-new">
          <Plus size={14} /> Neues Protokoll
        </Button>
      </div>
      {loading && <div className="text-xs text-slate-400 italic">lade…</div>}
      {!loading && items.length === 0 && (
        <div className="text-xs text-slate-400 italic border border-dashed border-slate-200 rounded p-4 text-center">
          Noch keine Protokolle vorhanden. Legen Sie z.&nbsp;B. ein Übergabeprotokoll an, wenn der Mieter einzieht.
        </div>
      )}
      {items.length > 0 && (
        <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-2.5 py-1.5">Typ</th>
                <th className="text-left px-2.5 py-1.5">Termin</th>
                <th className="text-left px-2.5 py-1.5">Ort</th>
                <th className="text-left px-2.5 py-1.5">Status</th>
                <th className="text-right px-2.5 py-1.5 pr-3">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`panel-protocol-${p.id}`}>
                  <td className="px-2.5 py-1.5 font-medium text-slate-800">{TYPE_LABELS[p.protocol_type] || p.protocol_type}</td>
                  <td className="px-2.5 py-1.5 tabular-nums text-slate-600">
                    {p.appointment_date ? formatDate(p.appointment_date) : "—"}{p.appointment_time ? ` ${p.appointment_time}` : ""}
                  </td>
                  <td className="px-2.5 py-1.5 text-slate-600 truncate max-w-[180px]">{p.location || "—"}</td>
                  <td className="px-2.5 py-1.5">
                    {p.status === "signed" ? (
                      <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded-full text-[10px] border border-emerald-200 bg-emerald-50 text-emerald-800">
                        <ShieldCheck size={9} /> Signiert
                      </span>
                    ) : (
                      <span className="inline-flex items-center h-5 px-1.5 rounded-full text-[10px] border border-amber-200 bg-amber-50 text-amber-800">Entwurf</span>
                    )}
                  </td>
                  <td className="px-2.5 py-1.5 pr-3 text-right">
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openPdf(p.id, false)}
                            title="Vorschau" data-testid={`panel-preview-${p.id}`}><Eye size={12} /></Button>
                    {p.status === "signed" && (
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openPdf(p.id, true)}
                              title="Signierte PDF"><Download size={12} /></Button>
                    )}
                    {p.status !== "signed" && (
                      <>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openBlank(p.id)}
                                title="Blanko-Formular zum Ausdrucken"
                                data-testid={`panel-blank-${p.id}`}><Printer size={12} /></Button>
                        <input
                          ref={(el) => { if (el) fileInputRefs.current[p.id] = el; }}
                          type="file"
                          accept="application/pdf,image/*"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = "";
                            if (f) doUploadSigned(p.id, f);
                          }}
                          data-testid={`panel-upload-signed-input-${p.id}`}
                        />
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => triggerUpload(p.id)}
                                title="Unterschriebenes Original hochladen (Blanko wird ersetzt)"
                                data-testid={`panel-upload-signed-${p.id}`}><UploadSimple size={12} /></Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEdit(p.id)}
                                title="Bearbeiten"><PencilSimple size={12} /></Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => del(p.id)}
                                title="Löschen"><Trash size={12} /></Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ProtocolWizard open={openWizard} onOpenChange={setOpenWizard}
                      initial={wizardInitial} onSaved={load}
                      lockTenancy={!!tenancyId} />

      {/* iter160k — Hochgeladene Protokoll-Dokumente (category=protocol) */}
      {uploadedDocs.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
            Hochgeladene Protokoll-Dokumente ({uploadedDocs.length})
          </div>
          <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-2.5 py-1.5">Titel</th>
                  <th className="text-left px-2.5 py-1.5">Datei</th>
                  <th className="text-left px-2.5 py-1.5">Hochgeladen am</th>
                  <th className="text-right px-2.5 py-1.5 pr-3">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {uploadedDocs.map((d) => (
                  <tr key={d.id} className="border-t border-slate-100">
                    <td className="px-2.5 py-1.5 font-medium text-slate-800">{d.title || d.original_filename}</td>
                    <td className="px-2.5 py-1.5 text-slate-600 truncate max-w-[220px]">{d.original_filename || "—"}</td>
                    <td className="px-2.5 py-1.5 tabular-nums text-slate-600">{formatDate(d.document_date || d.created_at)}</td>
                    <td className="px-2.5 py-1.5 pr-3 text-right">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setPreviewId(d.id)}
                              title="Ansehen" data-testid={`protocol-doc-view-${d.id}`}><Eye size={12} /></Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6"
                              onClick={async () => { try { const { data } = await api.get(`/documents/${d.id}/download`, { responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = d.original_filename || "Protokoll"; a.click(); } catch (e) { toast.error(formatApiError(e)); } }}
                              title="Herunterladen" data-testid={`protocol-doc-download-${d.id}`}><Download size={12} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-slate-500 italic mt-1">
            Tipp: Um ein Dokument hier anzuzeigen, im Dokumenten-Reiter beim Upload die Kategorie „Übergabeprotokoll" wählen.
          </div>
        </div>
      )}
      {previewId && <DocumentPreviewDrawer docId={previewId} onClose={() => { setPreviewId(null); load(); }} />}
    </div>
  );
}
